"use client";
import React, { useDeferredValue, useEffect, useRef, useState, useTransition } from "react";
import styles from "./search.module.css";
import watch from "../watchnow/watch.module.css";
import DramaList from "../component/DramaList";
import LoadMore from "../component/loadmore/LoadMore";
import { ClearIcon, NoSearchResultIcon, SearchIcon } from "../component/videoplayer/PlayerIcons";
import { useTrendingStore } from "../store/trendingStore";
import InFeedAd from "../component/ad/InFeedAd";


async function fetchMoreDramas(inputval: string, currentpage: number, signal?: AbortSignal) {
  try {
    const res = await fetch(`https://dramasstory.com/web/new_combo_search.php?srch=${inputval}&page=${currentpage}`, { signal });
    const data = await res.json();
    return data?.data?.Data[0].data || [];
  } catch (error) {
    // Swallow abort errors silently
    if ((error as DOMException)?.name === 'AbortError') {
      return [];
    }
    console.error('Error fetching dramas:', error);
    return [];
  }
}

function SearchFilter() {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Defer the search term to keep typing responsive under heavy renders
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [noResult, setNoResult] = useState(false);
  const [, startTransition] = useTransition();
  // Cache to avoid duplicate network calls for the same query within the session
  const cacheRef = useRef<Map<string, any[]>>(new Map());
  // AbortController to cancel in-flight requests when the term changes quickly
  const abortRef = useRef<AbortController | null>(null);
  // Monotonic request id to ignore out-of-order responses (extra safety)
  const requestIdRef = useRef(0);
  
  const [lastSearchedTerm, setLastSearchedTerm] = useState('');

  // Use Zustand store for trending data
  const { trending, fetchTrending } = useTrendingStore();

  useEffect(() => {
    // Fetch trending data when component mounts
    fetchTrending();
  }, [fetchTrending]);



  useEffect(() => {
    // Clear any existing timeout
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Cancel any in-flight request when the term changes
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    // If the search term is empty or only contains spaces, clear the data and pagination state
    if (deferredSearchTerm === '' || deferredSearchTerm.trim() === '') {
      setData([]);
      setHasMore(false);
      setCurrentPage(1);
      setNoResult(false);
      setLastSearchedTerm('');
      setLoading(false);
      return;
    }
    setLoading(false);
    if (deferredSearchTerm.trim().length < 3) return;

    // Set a new timer to perform the search after a delay
    debounceTimerRef.current = setTimeout(async () => {
      const key = deferredSearchTerm.trim().toLowerCase();

      // Serve from cache if available to avoid network and re-render fast
      const cached = cacheRef.current.get(key);
      if (cached) {
        startTransition(() => {
          setData(cached);
          setNoResult(cached.length === 0);
          setHasMore(cached.length === 10);
          setCurrentPage(1);
          setLoading(false);
          setLastSearchedTerm(deferredSearchTerm.trim());
        });
        return;
      }

      // Otherwise, fire a new request with cancellation & stale-response guard
      setLoading(true);
      const controller = new AbortController();
      abortRef.current = controller;
      const reqId = ++requestIdRef.current;

      const newDramas = await fetchMoreDramas(deferredSearchTerm.trim(), 1, controller.signal);
      // Ignore if a newer request started or this one was aborted
      if (reqId !== requestIdRef.current || controller.signal.aborted) {
        return;
      }
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const mappedDramas = newDramas.map((item: any) => ({
        type: "Drama",
        d_id: item.id,
        d_name: item.drama_name,
        d_image: item.image_url,
        unlock_count: item.unlock_epi_count,
        total_d_stream: item.total_episodes,
        gradient: item.gradient,
        d_desc: item.d_desc || ''
      }));

      // Cache the successful result for this key
      cacheRef.current.set(key, mappedDramas);

      startTransition(() => {
        setData(mappedDramas);
        setNoResult(mappedDramas.length === 0);
        setHasMore(mappedDramas.length === 10);
        setLoading(false);
        setCurrentPage(1);
        setLastSearchedTerm(deferredSearchTerm.trim());
      });
    }, 500);

    // Cleanup function to clear the timeout if the component unmounts
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setLoading(false);
    };
  }, [deferredSearchTerm]);



  const handleLoadMore = async () => {
    if (!loadMoreLoading && hasMore) {
      setLoadMoreLoading(true);
      const nextPage = currentPage + 1;
      const newDramas = await fetchMoreDramas(searchTerm.trim(), nextPage);
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const mappedDramas = newDramas.map((item: any) => ({
        type: "Drama",
        d_id: item.id,
        d_name: item.drama_name,
        d_image: item.image_url,
        unlock_count: item.unlock_epi_count,
        total_d_stream: item.total_episodes,
        gradient: item.gradient
      }));

      setData(prevData => [...prevData, ...mappedDramas]);
      setHasMore(mappedDramas.length === 10);
      setLoadMoreLoading(false);
      setCurrentPage(nextPage);
    }
  };

  const checktrending = () => { 
    return (trending.map(e => e.toLowerCase())).includes(searchTerm.trim().toLowerCase()) 
  };


  return (
    <>
      <div className={watch.watchNow}>
        <h2 className={watch.searchTitle}>Discover & Stream Your Favorite Dramas</h2>
        <h2 style={{ display: "none" }}>Saved List</h2>
      </div>
      <div className={styles.filterSec}>
        <div className="container">
          {/* search area  */}
          <div className={styles.searchBar}>
            <input
              type="search"
              name="search"
              id="search"
              placeholder="Search Drama... (min 3 characters)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className={styles.searchBarIcon}>
              {searchTerm.length === 0 && <SearchIcon />}
              {searchTerm.length > 0 && <span onClick={() => setSearchTerm('')} style={{ cursor: 'pointer' , display: 'inline-flex' }}>
                <ClearIcon />
              </span>}
            </span>
          </div>

          {/* loader */}
          <div style={{ textAlign: 'center', color: '#888', visibility: loading && !loadMoreLoading ? 'visible' : 'hidden' }}>
            <div className={styles.loaderSearch} style={{ visibility: loading && !loadMoreLoading ? 'visible' : 'hidden' }}></div>
          </div>

          {/* trending search */}
          {(data.length === 0 || checktrending()) && trending.length > 0 && <div className={styles.TredListSec}>
            <h3 className={styles.trendingSearch}>Trending Search</h3>
            <div className={styles.TredList}>
              {trending.map(item => <span key={item}
                className={data.length > 0 && item.toLowerCase() === lastSearchedTerm.toLowerCase() ? styles.active : ''}
                onClick={() => setSearchTerm(item)} >
                {item}</span>)}
            </div>
          </div>}

          {/* search results */}
          {data.length > 0 && <h3 style={{ marginBottom: '16px' }} className={styles.trendingSearch}>Search Results:</h3>}
          <InFeedAd style={{ margin: '20px 0' }} />
          {data.length > 0 && <DramaList dramas={data} allEp={true} />}

          {!loading && hasMore && data.length > 0 && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <LoadMore onClick={handleLoadMore} isLoading={loadMoreLoading} />
            </div>
          )}
          {!hasMore && data.length > 10 && (
            <p style={{ textAlign: 'center', marginTop: '20px', paddingBottom: '20px', color: '#555' }}>
              All results loaded.
            </p>
          )}

          {noResult && <div className={styles.notfound}>
            <NoSearchResultIcon />
            <div className="notFoundContent">
              <h2>No Drama Matches Your Search!</h2>
              <p>Looks like this story isn&#39;t in our library.</p>
            </div>
          </div>}


        </div>
      </div>
    </>
  );
}

export default SearchFilter;
